import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastOptions {
  title?: string;
  /** The main message. Keep it plain and friendly. */
  body?: ReactNode;
  tone?: ToastTone;
  /** Milliseconds before auto-dismiss. Use 0 to keep it until dismissed. */
  duration?: number;
}

interface ToastRecord extends ToastOptions {
  id: number;
  tone: ToastTone;
}

interface ToastApi {
  toast: (opts: ToastOptions) => number;
  success: (body: ReactNode, title?: string) => number;
  error: (body: ReactNode, title?: string) => number;
  info: (body: ReactNode, title?: string) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_ICON: Record<ToastTone, Parameters<typeof Icon>[0]['name']> = {
  success: 'check-circle',
  error: 'alert',
  info: 'info',
};

/** Wrap the app so any component can raise a toast via `useToast()`. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const idRef = useRef(0);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (opts: ToastOptions) => {
      const id = ++idRef.current;
      const record: ToastRecord = { ...opts, id, tone: opts.tone ?? 'info' };
      setToasts((list) => [...list, record]);
      const duration = opts.duration ?? 4500;
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(() => {
    // Build options without an explicit `title: undefined`, which
    // exactOptionalPropertyTypes forbids on the optional `title` field.
    const raise = (tone: ToastTone) => (body: ReactNode, title?: string): number =>
      toast(title === undefined ? { body, tone } : { body, title, tone });
    return {
      toast,
      dismiss,
      success: raise('success'),
      error: raise('error'),
      info: raise('info'),
    };
  }, [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="pt-toast-region" role="region" aria-label="Notifications" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`pt-toast pt-toast--${t.tone}`}>
              <span className="pt-toast__icon">
                <Icon name={TONE_ICON[t.tone]} size={20} />
              </span>
              <div className="pt-toast__content">
                {t.title ? <div className="pt-toast__title">{t.title}</div> : null}
                {t.body ? <div className="pt-toast__body">{t.body}</div> : null}
              </div>
              <IconButton
                label="Dismiss"
                size="sm"
                icon={<Icon name="close" size={16} />}
                onClick={() => dismiss(t.id)}
              />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/** Access the toast API. Must be used under a ToastProvider. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside a ToastProvider');
  return ctx;
}
